/**
 * MCP Validation Tool Handlers
 *
 * Implementations for model validation and analysis tools.
 */

import { validateDocument } from '../../parser';
import { runSanityChecks, getAvailableChecks } from '../../ai/sanityChecks';
import type { IDFObject } from '../../parser';
import type { MCPToolResult, ValidationResult, AutoFixOperation } from '../types';
import type { HandlerContext } from './readHandlers';

/**
 * Get object name from first field
 */
function getObjectName(obj: IDFObject): string | null {
  return obj.fields[0]?.value || null;
}

/**
 * Handle validate_model tool
 */
export function handleValidateModel(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded for validation' };
  }

  const includeWarnings = args["includeWarnings"] !== false;

  const result = validateDocument(ctx.document, ctx.schema);

  const errors = result.errors.map((e) => ({
    message: e.message,
    line: e.line,
    severity: e.severity,
  }));

  const warnings = includeWarnings
    ? result.warnings.map((w) => ({
        message: w.message,
        line: w.line,
        severity: w.severity,
      }))
    : [];

  const validation: ValidationResult = {
    isValid: result.errors.length === 0,
    errorCount: result.errors.length,
    warningCount: result.warnings.length,
    errors: [...errors, ...warnings],
  };

  return {
    success: true,
    content: validation,
  };
}

/**
 * Handle validate_object tool
 */
export function handleValidateObject(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded for validation' };
  }

  const objectId = args["objectId"] as string;
  const obj = ctx.getObjectById(objectId);

  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const schemaType = ctx.schema.objectTypes.get(obj.className.toLowerCase());

  if (!schemaType) {
    return {
      success: true,
      content: {
        objectId,
        className: obj.className,
        isValid: false,
        errors: [{ message: `Unknown object class: ${obj.className}`, severity: 'error' }],
      },
    };
  }

  const fieldErrors: Array<{
    fieldIndex: number;
    fieldName: string;
    message: string;
    severity: 'error' | 'warning';
  }> = [];

  // Validate each field
  for (let i = 0; i < Math.max(obj.fields.length, schemaType.minFields); i++) {
    const field = obj.fields[i];
    const schemaField = schemaType.fields[i];

    if (!schemaField) {
      // Extra fields beyond schema
      if (schemaType.extensible === 0 && i >= schemaType.fields.length) {
        fieldErrors.push({
          fieldIndex: i,
          fieldName: `Field ${String(i + 1)}`,
          message: 'Extra field beyond schema definition',
          severity: 'warning',
        });
      }
      continue;
    }

    const value = field?.value || '';

    // Check required fields
    if (schemaField.required && !value) {
      fieldErrors.push({
        fieldIndex: i,
        fieldName: schemaField.name,
        message: 'Required field is empty',
        severity: 'error',
      });
      continue;
    }

    if (!value) continue;

    // Check numeric types
    if (schemaField.type === 'real' || schemaField.type === 'integer') {
      const lowerValue = value.toLowerCase();
      if (lowerValue !== 'autosize' && lowerValue !== 'autocalculate') {
        const num = parseFloat(value);
        if (isNaN(num)) {
          fieldErrors.push({
            fieldIndex: i,
            fieldName: schemaField.name,
            message: `Expected ${schemaField.type}, got: ${value}`,
            severity: 'error',
          });
        } else {
          // Check range
          if (schemaField.minimum !== undefined) {
            const minOk = schemaField.exclusiveMinimum ? num > schemaField.minimum : num >= schemaField.minimum;
            if (!minOk) {
              fieldErrors.push({
                fieldIndex: i,
                fieldName: schemaField.name,
                message: `Value ${String(num)} is below minimum ${String(schemaField.minimum)}`,
                severity: 'error',
              });
            }
          }
          if (schemaField.maximum !== undefined) {
            const maxOk = schemaField.exclusiveMaximum ? num < schemaField.maximum : num <= schemaField.maximum;
            if (!maxOk) {
              fieldErrors.push({
                fieldIndex: i,
                fieldName: schemaField.name,
                message: `Value ${String(num)} is above maximum ${String(schemaField.maximum)}`,
                severity: 'error',
              });
            }
          }
        }
      }
    }

    // Check choice fields
    if (schemaField.type === 'choice' && schemaField.choices) {
      const lowerValue = value.toLowerCase();
      const validChoice = schemaField.choices.some((c) => c.toLowerCase() === lowerValue);
      if (!validChoice) {
        fieldErrors.push({
          fieldIndex: i,
          fieldName: schemaField.name,
          message: `Invalid choice: ${value}. Valid options: ${schemaField.choices.join(', ')}`,
          severity: 'error',
        });
      }
    }

    // Check object-list references
    if (schemaField.type === 'object-list' && schemaField.objectList) {
      const lowerValue = value.toLowerCase();
      const refExists = ctx.document.objects.some(
        (o) => getObjectName(o)?.toLowerCase() === lowerValue
      );
      if (!refExists) {
        fieldErrors.push({
          fieldIndex: i,
          fieldName: schemaField.name,
          message: `Reference not found: ${value}`,
          severity: 'warning',
        });
      }
    }
  }

  return {
    success: true,
    content: {
      objectId,
      className: obj.className,
      objectName: getObjectName(obj),
      isValid: fieldErrors.filter((e) => e.severity === 'error').length === 0,
      errorCount: fieldErrors.filter((e) => e.severity === 'error').length,
      warningCount: fieldErrors.filter((e) => e.severity === 'warning').length,
      fieldErrors,
    },
  };
}

/**
 * Handle run_sanity_checks tool
 */
export function handleRunSanityChecks(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  // We need raw content for sanity checks
  // Serialize the document if we don't have raw content
  const rawContent = ctx.document.objects.map((obj) => {
    const fields = obj.fields.map((f) => f.value).join(',\n  ');
    return `${obj.className},\n  ${fields};`;
  }).join('\n\n');

  const checkIds = args["checkIds"] as string[] | undefined;

  let results = runSanityChecks(rawContent);

  // Filter by check IDs if specified
  if (checkIds && checkIds.length > 0) {
    results = results.filter((r) => checkIds.includes(r.id));
  }

  return {
    success: true,
    content: {
      checkCount: results.length,
      checks: results.map((r) => ({
        id: r.id,
        name: r.name,
        severity: r.severity,
        message: r.message,
        suggestion: r.suggestion,
        affectedObjects: r.affectedObjects,
        autoFixAvailable: r.autoFixAvailable,
      })),
      availableChecks: getAvailableChecks(),
    },
  };
}

/**
 * Handle get_available_auto_fixes tool
 */
export function handleGetAvailableAutoFixes(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const fixes: AutoFixOperation[] = [];

  // Check for missing Version
  const hasVersion = ctx.document.objects.some(
    (obj) => obj.className.toLowerCase() === 'version'
  );
  if (!hasVersion) {
    fixes.push({
      id: 'missing-version',
      description: 'Add Version object with current schema version',
      changes: [{ type: 'add', className: 'Version' }],
      confidence: 'high',
    });
  }

  // Check for missing Building
  const hasBuilding = ctx.document.objects.some(
    (obj) => obj.className.toLowerCase() === 'building'
  );
  if (!hasBuilding) {
    fixes.push({
      id: 'missing-building',
      description: 'Add Building object with default values',
      changes: [{ type: 'add', className: 'Building' }],
      confidence: 'high',
    });
  }

  // Check for missing SimulationControl
  const hasSimControl = ctx.document.objects.some(
    (obj) => obj.className.toLowerCase() === 'simulationcontrol'
  );
  if (!hasSimControl) {
    fixes.push({
      id: 'missing-simulation-control',
      description: 'Add SimulationControl object with default values',
      changes: [{ type: 'add', className: 'SimulationControl' }],
      confidence: 'high',
    });
  }

  // Check for missing GlobalGeometryRules
  const hasGeomRules = ctx.document.objects.some(
    (obj) => obj.className.toLowerCase() === 'globalgeometryrules'
  );
  if (!hasGeomRules && ctx.document.objects.some((obj) => obj.className.toLowerCase().includes('surface'))) {
    fixes.push({
      id: 'missing-geometry-rules',
      description: 'Add GlobalGeometryRules object (required for surfaces)',
      changes: [{ type: 'add', className: 'GlobalGeometryRules' }],
      confidence: 'medium',
    });
  }

  return {
    success: true,
    content: {
      availableFixCount: fixes.length,
      fixes,
    },
  };
}

/**
 * Handle check_field_value tool
 */
export function handleCheckFieldValue(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded' };
  }

  const className = args["className"] as string;
  const fieldIndex = args["fieldIndex"] as number;
  const value = args["value"] as string;

  const schemaType = ctx.schema.objectTypes.get(className.toLowerCase());
  if (!schemaType) {
    return { success: false, error: `Unknown object class: ${className}` };
  }

  const schemaField = schemaType.fields[fieldIndex];
  if (!schemaField) {
    return { success: false, error: `Field index ${String(fieldIndex)} out of range for ${className}` };
  }

  const issues: Array<{ message: string; severity: 'error' | 'warning' }> = [];
  const suggestions: string[] = [];

  // Check type compatibility
  if (schemaField.type === 'real' || schemaField.type === 'integer') {
    const lowerValue = value.toLowerCase();
    if (lowerValue !== 'autosize' && lowerValue !== 'autocalculate' && lowerValue !== '') {
      const num = parseFloat(value);
      if (isNaN(num)) {
        issues.push({ message: `Expected ${schemaField.type}, got: ${value}`, severity: 'error' });
        suggestions.push('Enter a valid number');
      } else {
        if (schemaField.minimum !== undefined && num < schemaField.minimum) {
          issues.push({ message: `Value ${String(num)} below minimum ${String(schemaField.minimum)}`, severity: 'error' });
          suggestions.push(`Use a value >= ${String(schemaField.minimum)}`);
        }
        if (schemaField.maximum !== undefined && num > schemaField.maximum) {
          issues.push({ message: `Value ${String(num)} above maximum ${String(schemaField.maximum)}`, severity: 'error' });
          suggestions.push(`Use a value <= ${String(schemaField.maximum)}`);
        }
      }
    }

    if (schemaField.autosizable && lowerValue !== 'autosize') {
      suggestions.push('You can use "autosize" to let EnergyPlus calculate this value');
    }
    if (schemaField.autocalculatable && lowerValue !== 'autocalculate') {
      suggestions.push('You can use "autocalculate" to let EnergyPlus calculate this value');
    }
  }

  if (schemaField.type === 'choice' && schemaField.choices && value) {
    const lowerValue = value.toLowerCase();
    const validChoice = schemaField.choices.some((c) => c.toLowerCase() === lowerValue);
    if (!validChoice) {
      issues.push({ message: `Invalid choice: ${value}`, severity: 'error' });
      suggestions.push(`Valid options: ${schemaField.choices.join(', ')}`);
    }
  }

  if (schemaField.required && !value) {
    issues.push({ message: 'This field is required', severity: 'error' });
  }

  return {
    success: true,
    content: {
      className,
      fieldIndex,
      fieldName: schemaField.name,
      value,
      isValid: issues.filter((i) => i.severity === 'error').length === 0,
      issues,
      suggestions,
      fieldInfo: {
        type: schemaField.type,
        required: schemaField.required,
        default: schemaField.default,
        units: schemaField.units,
        minimum: schemaField.minimum,
        maximum: schemaField.maximum,
        choices: schemaField.choices,
      },
    },
  };
}

/**
 * Handle check_reference tool
 */
export function handleCheckReference(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const referenceName = args["referenceName"] as string;
  const expectedType = args["expectedType"] as string | undefined;

  const lowerName = referenceName.toLowerCase();

  const matchingObjects = ctx.document.objects.filter(
    (obj) => getObjectName(obj)?.toLowerCase() === lowerName
  );

  if (matchingObjects.length === 0) {
    return {
      success: true,
      content: {
        referenceName,
        isValid: false,
        message: `No object found with name: ${referenceName}`,
        suggestions: ctx.document.objects
          .filter((obj) => {
            const name = getObjectName(obj);
            return name && name.toLowerCase().includes(lowerName.substring(0, 3));
          })
          .slice(0, 5)
          .map((obj) => ({
            name: getObjectName(obj),
            className: obj.className,
          })),
      },
    };
  }

  if (expectedType) {
    const correctType = matchingObjects.some(
      (obj) => obj.className.toLowerCase() === expectedType.toLowerCase()
    );
    if (!correctType) {
      return {
        success: true,
        content: {
          referenceName,
          isValid: false,
          message: `Object "${referenceName}" exists but is not of expected type "${expectedType}"`,
          actualType: matchingObjects[0]?.className,
        },
      };
    }
  }

  return {
    success: true,
    content: {
      referenceName,
      isValid: true,
      matchCount: matchingObjects.length,
      matches: matchingObjects.map((obj) => ({
        id: obj.id,
        className: obj.className,
      })),
    },
  };
}

/**
 * Handle get_missing_required_objects tool
 */
export function handleGetMissingRequiredObjects(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded' };
  }

  const presentClasses = new Set(
    ctx.document.objects.map((obj) => obj.className.toLowerCase())
  );

  const missing: Array<{
    className: string;
    group: string;
    memo: string;
  }> = [];

  for (const [, objType] of ctx.schema.objectTypes) {
    if (objType.isRequired && !presentClasses.has(objType.name.toLowerCase())) {
      missing.push({
        className: objType.name,
        group: objType.group,
        memo: objType.memo.substring(0, 200),
      });
    }
  }

  return {
    success: true,
    content: {
      missingCount: missing.length,
      missing,
    },
  };
}

/**
 * Handle analyze_model_completeness tool
 */
export function handleAnalyzeModelCompleteness(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const presentClasses = new Set(
    ctx.document.objects.map((obj) => obj.className.toLowerCase())
  );

  const checks = {
    hasVersion: presentClasses.has('version'),
    hasBuilding: presentClasses.has('building'),
    hasSimulationControl: presentClasses.has('simulationcontrol'),
    hasRunPeriod: presentClasses.has('runperiod'),
    hasSite: presentClasses.has('site:location'),
    hasZones: ctx.document.objects.some((o) => o.className.toLowerCase() === 'zone'),
    hasSurfaces: ctx.document.objects.some((o) => o.className.toLowerCase().includes('surface')),
    hasConstructions: ctx.document.objects.some((o) => o.className.toLowerCase().startsWith('construction')),
    hasMaterials: ctx.document.objects.some(
      (o) => o.className.toLowerCase() === 'material' || o.className.toLowerCase().startsWith('material:')
    ),
    hasSchedules: ctx.document.objects.some((o) => o.className.toLowerCase().startsWith('schedule')),
    hasOutputVariables: ctx.document.objects.some(
      (o) => o.className.toLowerCase() === 'output:variable' || o.className.toLowerCase().startsWith('output:')
    ),
    hasTimestep: presentClasses.has('timestep'),
    hasHvac: ctx.document.objects.some(
      (o) => o.className.toLowerCase().includes('hvac') || o.className.toLowerCase().includes('airloop')
    ),
  };

  const issues: string[] = [];
  const recommendations: string[] = [];

  if (!checks.hasVersion) issues.push('Missing Version object');
  if (!checks.hasBuilding) issues.push('Missing Building object (required)');
  if (!checks.hasSimulationControl) recommendations.push('Add SimulationControl for explicit simulation settings');
  if (!checks.hasRunPeriod) issues.push('Missing RunPeriod - simulation cannot run without it');
  if (!checks.hasSite) recommendations.push('Add Site:Location for accurate solar calculations');
  if (!checks.hasZones) issues.push('No Zone objects found - model needs thermal zones');
  if (!checks.hasSurfaces && checks.hasZones) issues.push('Zones exist but no surfaces defined');
  if (!checks.hasConstructions && checks.hasSurfaces) issues.push('Surfaces exist but no constructions defined');
  if (!checks.hasMaterials && checks.hasConstructions) issues.push('Constructions exist but no materials defined');
  if (!checks.hasSchedules && checks.hasZones) recommendations.push('Add schedules for occupancy, lighting, and equipment');
  if (!checks.hasOutputVariables) recommendations.push('Add Output:Variable objects to capture simulation results');
  if (!checks.hasTimestep) recommendations.push('Add Timestep object for explicit timestep control');

  const completeness = Object.values(checks).filter(Boolean).length / Object.keys(checks).length;

  return {
    success: true,
    content: {
      completenessScore: Math.round(completeness * 100),
      isSimulationReady: issues.length === 0,
      checks,
      issues,
      recommendations,
    },
  };
}

/**
 * Handle get_suggested_values tool
 */
export function handleGetSuggestedValues(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded' };
  }

  const objectId = args["objectId"] as string;
  const fieldIndex = args["fieldIndex"] as number;

  const obj = ctx.getObjectById(objectId);
  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const schemaType = ctx.schema.objectTypes.get(obj.className.toLowerCase());
  const schemaField = schemaType?.fields[fieldIndex];

  if (!schemaField) {
    return { success: false, error: `Field index ${String(fieldIndex)} out of range` };
  }

  const suggestions: Array<{
    value: string;
    source: string;
    reason: string;
  }> = [];

  // Add default value if available
  if (schemaField.default) {
    suggestions.push({
      value: schemaField.default,
      source: 'schema_default',
      reason: 'Default value from IDD schema',
    });
  }

  // Add choices if available
  if (schemaField.choices) {
    for (const choice of schemaField.choices.slice(0, 5)) {
      suggestions.push({
        value: choice,
        source: 'schema_choice',
        reason: 'Valid choice from IDD schema',
      });
    }
  }

  // Add autosize/autocalculate if available
  if (schemaField.autosizable) {
    suggestions.push({
      value: 'autosize',
      source: 'autosizing',
      reason: 'Let EnergyPlus automatically size this value',
    });
  }
  if (schemaField.autocalculatable) {
    suggestions.push({
      value: 'autocalculate',
      source: 'autocalculate',
      reason: 'Let EnergyPlus automatically calculate this value',
    });
  }

  // For object-list fields, suggest existing objects
  if (schemaField.type === 'object-list' || schemaField.objectList) {
    const existingNames = ctx.document.objects
      .map((o) => getObjectName(o))
      .filter((n): n is string => n !== null)
      .slice(0, 10);

    for (const name of existingNames) {
      suggestions.push({
        value: name,
        source: 'existing_object',
        reason: 'Existing object in the model',
      });
    }
  }

  return {
    success: true,
    content: {
      objectId,
      fieldIndex,
      fieldName: schemaField.name,
      fieldType: schemaField.type,
      currentValue: obj.fields[fieldIndex]?.value || '',
      suggestions,
    },
  };
}

/**
 * Handle preview_changes tool
 */
export function handlePreviewChanges(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const changes = args["changes"] as Array<{
    type: 'add' | 'modify' | 'remove';
    objectId?: string;
    className?: string;
    fieldIndex?: number;
    value?: string;
  }>;

  const preview: Array<{
    change: typeof changes[0];
    impact: 'low' | 'medium' | 'high';
    warnings: string[];
  }> = [];

  for (const change of changes) {
    const warnings: string[] = [];
    let impact: 'low' | 'medium' | 'high' = 'low';

    if (change.type === 'remove' && change.objectId) {
      const obj = ctx.getObjectById(change.objectId);
      if (obj) {
        const name = getObjectName(obj);
        if (name) {
          // Check for references
          let refCount = 0;
          for (const otherObj of ctx.document.objects) {
            for (const field of otherObj.fields) {
              if (field.value.toLowerCase() === name.toLowerCase()) {
                refCount++;
              }
            }
          }
          if (refCount > 0) {
            warnings.push(`${String(refCount)} references to this object will be broken`);
            impact = 'high';
          }
        }
      }
    }

    if (change.type === 'modify' && change.objectId && change.fieldIndex !== undefined) {
      const obj = ctx.getObjectById(change.objectId);
      if (obj && change.fieldIndex === 0) {
        // Changing name field
        const oldName = obj.fields[0]?.value;
        if (oldName) {
          let refCount = 0;
          for (const otherObj of ctx.document.objects) {
            for (const field of otherObj.fields) {
              if (field.value.toLowerCase() === oldName.toLowerCase()) {
                refCount++;
              }
            }
          }
          if (refCount > 0) {
            warnings.push(`${String(refCount)} references to "${oldName}" may need updating`);
            impact = 'medium';
          }
        }
      }
    }

    preview.push({ change, impact, warnings });
  }

  const overallImpact = preview.some((p) => p.impact === 'high')
    ? 'high'
    : preview.some((p) => p.impact === 'medium')
    ? 'medium'
    : 'low';

  return {
    success: true,
    content: {
      changeCount: changes.length,
      overallImpact,
      preview,
    },
  };
}

/**
 * All validation handlers
 */
export const validationHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: HandlerContext) => MCPToolResult
> = {
  validate_model: handleValidateModel,
  validate_object: handleValidateObject,
  run_sanity_checks: handleRunSanityChecks,
  get_available_auto_fixes: handleGetAvailableAutoFixes,
  check_field_value: handleCheckFieldValue,
  check_reference: handleCheckReference,
  get_missing_required_objects: handleGetMissingRequiredObjects,
  analyze_model_completeness: handleAnalyzeModelCompleteness,
  get_suggested_values: handleGetSuggestedValues,
  preview_changes: handlePreviewChanges,
};
